/**
 * CM Wizard — Spreadsheet Import path.
 *
 * Flow:
 *   1. Pick CSV file
 *   2. Read as text in the browser
 *   3. Parse header row → show column mapping UI
 *   4. Client maps each CSV column → cm_input field
 *   5. POST /cm-wizard/spreadsheet with csvText + columnMap → backend
 *      parses, validates, commits in one step
 */

import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import CmWizardPathNav from "../components/CmWizardPathNav.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

const TARGET_FIELDS = [
  { key: "skuId", label: "SKU code", required: true, hint: "Matches your UltraCart SKUs" },
  { key: "cogs", label: "COGS (Cost of Goods)", required: true },
  { key: "fulfillmentCost", label: "Fulfillment cost", required: false },
  { key: "pickPackFee", label: "Pick/pack fee", required: false },
  { key: "storageFeePerMonth", label: "Storage / month", required: false },
  { key: "processingFeePct", label: "Processing fee %", required: false, hint: "0.029 = 2.9%" },
  { key: "processingFeeFixed", label: "Processing fee fixed", required: false },
  { key: "laborPerUnit", label: "Labor per unit", required: false },
  { key: "currency", label: "Currency (USD, CAD, ...)", required: false },
] as const;

type TargetField = (typeof TARGET_FIELDS)[number]["key"];

interface CommitResult {
  result: {
    inputsCreated: number;
    skusAffected: string[];
    customersStampedForRecalc: number;
    rowsSkipped: number;
    skippedReasons: string[];
  };
}

export default function CmWizardSpreadsheetPage(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [csvText, setCsvText] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [columnMap, setColumnMap] = useState<Partial<Record<TargetField, string>>>({});

  const headers = useMemo(() => parseCsvHeaders(csvText), [csvText]);
  const previewRows = useMemo(() => parseCsvPreview(csvText, 5), [csvText]);

  const commit = useMutation({
    mutationFn: (input: { csvText: string; columnMap: Partial<Record<TargetField, string>> }) =>
      api.post<CommitResult>("/cm-wizard/spreadsheet", {
        csvText: input.csvText,
        columnMap: input.columnMap,
        applyTo: "forward",
      }),
    onSuccess: (data) => {
      relockFinancialQueries(queryClient);
      const r = data.result;
      window.alert(
        `Imported: ${r.inputsCreated} SKUs.${
          r.rowsSkipped > 0 ? `\n${r.rowsSkipped} rows skipped:\n` + r.skippedReasons.slice(0, 5).join("\n") : ""
        }`,
      );
      setCsvText("");
      setFileName("");
      setColumnMap({});
    },
  });

  const readyToCommit = Boolean(columnMap.skuId && columnMap.cogs && csvText);

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24 }}>Import CM from spreadsheet</h1>
      <p style={{ color: "var(--body)", marginTop: 6 }}>
        Upload a CSV with your CM data. Map the columns. Save.
      </p>

      <div style={{ marginTop: 24 }}>
        <CmWizardPathNav />
      </div>

      {!csvText && (
        <div style={uploadStyles.box}>
          <input
            type="file"
            accept=".csv,text/csv"
            id="cm-csv-upload"
            style={{ display: "none" }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setFileName(f.name);
              setCsvText(await f.text());
            }}
          />
          <label htmlFor="cm-csv-upload" style={uploadStyles.pickButton}>
            Choose CSV file
          </label>
          <p style={uploadStyles.hint}>
            Header row required. One row per SKU. Columns can be in any order.
          </p>
        </div>
      )}

      {csvText && (
        <>
          <div style={fileInfoStyles.row}>
            <span style={{ color: "var(--good)" }}>{fileName}</span>
            <span style={{ color: "var(--body)", fontSize: 13 }}>
              {csvText.split("\n").length - 1} rows · {headers.length} columns
            </span>
            <button
              style={buttonStyles.secondary}
              onClick={() => {
                setCsvText("");
                setFileName("");
                setColumnMap({});
              }}
            >
              Pick a different file
            </button>
          </div>

          <h2 style={{ marginTop: 32, fontSize: 18 }}>Map your columns</h2>
          <p style={{ color: "var(--body)", marginTop: 4, fontSize: 13 }}>
            Tell us which column in your CSV holds each piece of info.
          </p>

          <div style={mapStyles.grid}>
            {TARGET_FIELDS.map((field) => (
              <div key={field.key} style={mapStyles.row}>
                <label style={mapStyles.label}>
                  {field.label}
                  {field.required && <span style={{ color: "var(--c2)", marginLeft: 4 }}>*</span>}
                  {"hint" in field && field.hint && (
                    <span style={mapStyles.hint}> — {field.hint}</span>
                  )}
                </label>
                <select
                  value={columnMap[field.key] ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setColumnMap((prev) => {
                      const next = { ...prev };
                      if (value === "") delete next[field.key];
                      else next[field.key] = value;
                      return next;
                    });
                  }}
                  style={mapStyles.select}
                >
                  <option value="">— not in file —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {previewRows.length > 0 && (
            <>
              <h3 style={{ marginTop: 32, fontSize: 14, color: "var(--body)" }}>
                Preview (first 5 rows of your file)
              </h3>
              <div style={tableStyles.scroll}>
                <table style={tableStyles.table}>
                  <thead>
                    <tr>
                      {headers.map((h) => (
                        <th key={h} style={tableStyles.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i}>
                        {headers.map((h) => (
                          <td key={h} style={tableStyles.td}>
                            {row[h] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {!readyToCommit && (
              <span style={{ color: "var(--c2)", fontSize: 13 }}>
                SKU code and COGS columns are required.
              </span>
            )}
            <button
              style={buttonStyles.primary}
              onClick={() => commit.mutate({ csvText, columnMap })}
              disabled={!readyToCommit || commit.isPending}
            >
              {commit.isPending ? "Importing…" : "Import + save"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function parseCsvHeaders(text: string): string[] {
  const firstLine = text.split("\n")[0]?.trim();
  if (!firstLine) return [];
  return parseCsvLine(firstLine);
}

function parseCsvPreview(text: string, n: number): Array<Record<string, string>> {
  const lines = text.split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0] ?? "");
  const out: Array<Record<string, string>> = [];
  for (let i = 1; i < Math.min(lines.length, n + 1); i++) {
    const cells = parseCsvLine(lines[i] ?? "");
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = cells[idx] ?? "";
    });
    out.push(row);
  }
  return out;
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let buf = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        buf += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        buf += ch;
      }
    } else {
      if (ch === ",") {
        cells.push(buf.trim());
        buf = "";
      } else if (ch === '"' && buf === "") {
        inQuotes = true;
      } else {
        buf += ch ?? "";
      }
    }
  }
  cells.push(buf.trim());
  return cells;
}

const uploadStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 24,
    padding: 32,
    background: "var(--surface)",
    border: "2px dashed var(--border)",
    borderRadius: 10,
    textAlign: "center",
    maxWidth: 540,
  },
  pickButton: {
    display: "inline-block",
    padding: "10px 20px",
    background: "var(--surface-2)",
    color: "var(--text)",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
  },
  hint: {
    color: "var(--body)",
    fontSize: 13,
    marginTop: 16,
    marginBottom: 0,
  },
};

const fileInfoStyles: Record<string, React.CSSProperties> = {
  row: {
    marginTop: 24,
    padding: "12px 16px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
};

const mapStyles: Record<string, React.CSSProperties> = {
  grid: {
    marginTop: 16,
    display: "grid",
    gap: 12,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 240px",
    gap: 16,
    alignItems: "center",
    padding: "10px 16px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 6,
  },
  label: {
    fontSize: 14,
  },
  hint: {
    color: "var(--body)",
    fontSize: 12,
  },
  select: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: 13,
    fontFamily: "inherit",
  },
};

const buttonStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondary: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
    marginLeft: "auto",
  },
};

const tableStyles: Record<string, React.CSSProperties> = {
  scroll: {
    marginTop: 12,
    overflowX: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    color: "var(--body)",
    fontWeight: 500,
    borderBottom: "1px solid var(--border)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 14px",
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  },
};
